import React, { useState, useRef } from 'react';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { useLocation } from 'wouter';
import { 
  ChevronLeft, 
  ChevronRight, 
  Maximize, 
  Minimize, 
  X, 
  Play, 
  CheckCircle2,
  FileText,
  ExternalLink,
  Home,
  ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Card } from '@/components/ui/card';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { 
  SHIFTFrameworkDiagram, 
  GLP1SignalingDiagram, 
  AutophagyDiagram, 
  MultiAgonistDiagram, 
  InflammationPathwayDiagram 
} from '@/components/diagrams/GLP1Diagrams';

// Define slide interface
interface Slide {
  id: number;
  title: string;
  content: React.ReactNode;
}

// Custom components for the lecture
const KeyTakeaway = ({ children, isFullscreen = false }: { children: React.ReactNode; isFullscreen?: boolean }) => {
  return (
    <div className={`relative overflow-hidden my-4 ${
      isFullscreen 
        ? 'bg-gradient-to-r from-blue-900/30 via-indigo-900/30 to-slate-800/30 border border-blue-600/50' 
        : 'bg-gradient-to-r from-blue-50 via-indigo-50 to-slate-50 border border-blue-200'
    } rounded-lg p-4`}>
      <div className={`absolute top-0 left-0 w-1 h-full ${
        isFullscreen ? 'bg-gradient-to-b from-blue-400 to-indigo-500' : 'bg-gradient-to-b from-blue-500 to-indigo-600'
      }`}></div>
      <div className="flex items-start">
        <div className="flex-shrink-0 mt-1">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
            isFullscreen ? 'bg-gradient-to-br from-blue-400 to-indigo-500' : 'bg-gradient-to-br from-blue-500 to-indigo-600'
          }`}>
            <CheckCircle2 className="h-3 w-3 text-white" />
          </div>
        </div>
        <div className="ml-3">
          <h4 className={`text-sm font-bold mb-1 tracking-tight ${
            isFullscreen ? 'text-slate-800' : 'text-slate-900' // Darker text for fullscreen
          }`}>Key Takeaway</h4>
          <p className={`leading-relaxed text-xs ${
            isFullscreen ? 'text-slate-700' : 'text-slate-700' // Darker text for fullscreen
          }`}>{children}</p>
        </div>
      </div>
    </div>
  );
};

// Content renderer that passes fullscreen state to KeyTakeaway and diagram components
const renderSlideContent = (content: React.ReactNode, isFullscreen: boolean): React.ReactNode => {
  if (React.isValidElement(content)) {
    const diagramComponents = [
      SHIFTFrameworkDiagram, 
      GLP1SignalingDiagram, 
      AutophagyDiagram, 
      MultiAgonistDiagram, 
      InflammationPathwayDiagram
    ];
    
    // Handle the root element styling
    let newProps = { ...content.props };
    if (content.props.className) {
      // Update background and text colors for fullscreen
      if (isFullscreen) {
        newProps.className = content.props.className
          .replace(/bg-blue-50/g, 'bg-blue-100/20')
          .replace(/bg-green-50/g, 'bg-green-100/20')
          .replace(/bg-red-50/g, 'bg-red-100/20')
          .replace(/bg-purple-50/g, 'bg-purple-100/20')
          .replace(/bg-yellow-50/g, 'bg-yellow-100/20')
          .replace(/bg-orange-50/g, 'bg-orange-100/20')
          .replace(/bg-gray-50/g, 'bg-gray-200/20')
          .replace(/bg-black/g, 'bg-transparent')
          .replace(/bg-gray-900/g, 'bg-transparent')
          .replace(/bg-slate-900/g, 'bg-transparent')
          .replace(/text-blue-800/g, 'text-blue-200')
          .replace(/text-green-800/g, 'text-green-200')
          .replace(/text-red-800/g, 'text-red-200')
          .replace(/text-purple-800/g, 'text-purple-200')
          .replace(/text-yellow-800/g, 'text-yellow-200')
          .replace(/text-orange-800/g, 'text-orange-200')
          .replace(/text-blue-700/g, 'text-blue-300')
          .replace(/text-green-700/g, 'text-green-300')
          .replace(/text-red-700/g, 'text-red-300')
          .replace(/text-purple-700/g, 'text-purple-300')
          .replace(/text-yellow-700/g, 'text-yellow-300')
          .replace(/text-orange-700/g, 'text-orange-300')
          .replace(/border-blue-200/g, 'border-blue-500/50')
          .replace(/border-green-200/g, 'border-green-500/50')
          .replace(/border-red-200/g, 'border-red-500/50')
          .replace(/border-purple-200/g, 'border-purple-500/50')
          .replace(/border-yellow-200/g, 'border-yellow-500/50')
          .replace(/border-orange-200/g, 'border-orange-500/50');
      }
    }
    
    return React.cloneElement(content as React.ReactElement, {
      ...newProps,
      children: React.Children.map(content.props.children, (child: React.ReactNode) => {
        if (React.isValidElement(child)) {
          // Handle KeyTakeaway components
          if (child.type === KeyTakeaway) {
            return React.cloneElement(child as React.ReactElement, { isFullscreen });
          }
          // Handle diagram components
          if (diagramComponents.includes(child.type as any)) {
            return React.cloneElement(child as React.ReactElement, { isFullscreen });
          }
          // Handle elements with className prop for styling updates
          if (child.props?.className && isFullscreen) {
            const updatedClassName = child.props.className
              .replace(/bg-blue-50/g, 'bg-blue-100/20')
              .replace(/bg-green-50/g, 'bg-green-100/20')
              .replace(/bg-red-50/g, 'bg-red-100/20')
              .replace(/bg-purple-50/g, 'bg-purple-100/20')
              .replace(/bg-yellow-50/g, 'bg-yellow-100/20')
              .replace(/bg-orange-50/g, 'bg-orange-100/20')
              .replace(/bg-gray-50/g, 'bg-gray-200/20')
              .replace(/bg-black/g, 'bg-transparent')
              .replace(/bg-gray-900/g, 'bg-transparent')
              .replace(/bg-slate-900/g, 'bg-transparent')
              .replace(/text-blue-800/g, 'text-blue-200')
              .replace(/text-green-800/g, 'text-green-200')
              .replace(/text-red-800/g, 'text-red-200')
              .replace(/text-purple-800/g, 'text-purple-200')
              .replace(/text-yellow-800/g, 'text-yellow-200')
              .replace(/text-orange-800/g, 'text-orange-200')
              .replace(/text-blue-700/g, 'text-blue-300')
              .replace(/text-green-700/g, 'text-green-300')
              .replace(/text-red-700/g, 'text-red-300')
              .replace(/text-purple-700/g, 'text-purple-300')
              .replace(/text-yellow-700/g, 'text-yellow-300')
              .replace(/text-orange-700/g, 'text-orange-300')
              .replace(/border-blue-200/g, 'border-blue-500/50')
              .replace(/border-green-200/g, 'border-green-500/50')
              .replace(/border-red-200/g, 'border-red-500/50')
              .replace(/border-purple-200/g, 'border-purple-500/50')
              .replace(/border-yellow-200/g, 'border-yellow-500/50')
              .replace(/border-orange-200/g, 'border-orange-500/50');
              
            return React.cloneElement(child as React.ReactElement, {
              className: updatedClassName,
              children: child.props?.children ? renderSlideContent(child.props.children, isFullscreen) : child.props?.children
            });
          }
          // Recursively handle nested components
          if (child.props?.children) {
            return React.cloneElement(child as React.ReactElement, {
              children: renderSlideContent(child.props.children, isFullscreen)
            });
          }
        }
        return child;
      })
    });
  }
  return content;
};

// Slideshow component
const Slideshow = ({ slides }: { slides: Slide[] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const slideshowRef = useRef<HTMLDivElement>(null);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (slideshowRef.current?.requestFullscreen) {
        slideshowRef.current.requestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  // Add fullscreen change event listener and keyboard navigation
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isFullscreen) {
        if (e.key === 'ArrowRight' || e.key === ' ') {
          e.preventDefault();
          nextSlide();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          prevSlide();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          toggleFullscreen();
        }
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  return (
    <div 
      ref={slideshowRef} 
      className={`w-full border border-slate-200 rounded-lg shadow-sm overflow-hidden mb-8 transition-all duration-300 ${
        isFullscreen 
          ? 'fixed inset-0 z-50 bg-white overflow-y-auto max-w-none' 
          : 'max-w-none bg-white'
      }`}
    >
      <div className={`p-4 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-slate-50 flex justify-between items-center ${
        isFullscreen ? 'sticky top-0 z-10' : ''
      }`}>
        <h3 className="text-base font-medium text-slate-800">Microdosing GLP-1s 2.0 - Advanced Strategies</h3>
        <div className="flex items-center gap-4">
          <div className="text-sm text-slate-500 font-medium">
            Slide {currentSlide + 1} of {slides.length}
          </div>
          <Button 
            onClick={toggleFullscreen}
            variant="outline"
            size="sm"
            className="gap-1.5"
          >
            {isFullscreen ? (
              <>
                <Minimize className="h-4 w-4" />
                Exit Fullscreen
              </>
            ) : (
              <>
                <Maximize className="h-4 w-4" />
                Fullscreen
              </>
            )}
          </Button>
        </div>
      </div>
      
      <div className={`${
        isFullscreen 
          ? 'p-8 md:p-12 lg:p-16 max-w-7xl mx-auto' 
          : 'p-6 md:p-8 lg:p-10'
      }`}>
        <h2 className={`font-semibold text-slate-900 mb-6 tracking-tight leading-tight ${
          isFullscreen ? 'text-2xl md:text-3xl' : 'text-xl md:text-2xl'
        }`}>
          {slides[currentSlide].title}
        </h2>
        <div className={`mb-6 overflow-y-auto pr-2 thin-scrollbar ${
          isFullscreen 
            ? 'min-h-[60vh] max-h-[70vh] text-base' 
            : 'min-h-[500px] max-h-[600px] text-sm'
        }`}>
          <div className="text-slate-700 leading-relaxed">
            {renderSlideContent(slides[currentSlide].content, isFullscreen)}
          </div>
        </div>
      </div>
      
      <div className={`px-6 md:px-8 lg:px-10 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between ${
        isFullscreen ? 'sticky bottom-0 z-10' : ''
      }`}>
        <button 
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className={`flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors ${
            currentSlide === 0 ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <ChevronLeft className="h-5 w-5 mr-1.5" />
          Previous
        </button>
        
        <div className="flex space-x-2.5">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={index === currentSlide 
                ? "h-2.5 w-2.5 rounded-full bg-blue-600 shadow-sm" 
                : "h-2.5 w-2.5 rounded-full bg-slate-300 hover:bg-slate-400 transition-colors"}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
        
        <button 
          onClick={nextSlide}
          disabled={currentSlide === slides.length - 1}
          className={`flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors ${
            currentSlide === slides.length - 1 ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          Next
          <ChevronRight className="h-5 w-5 ml-1.5" />
        </button>
      </div>
    </div>
  );
};

const MicrodosingGLP1s2: React.FC = () => {
  const [, setLocation] = useLocation();

  // Define lecture slides - condensed version with key slides
  const lectureSlides: Slide[] = [
    {
      id: 1,
      title: "Meet Your Guide – Anant Vinjamoori, MD MBA",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            A decade ago I was treating heart-attack survivors at Harvard hospitals; today I help clinics keep people from ever getting there in the first place. I'm Anant Vinjamoori—longevity physician, entrepreneur, and President of <strong>Next Generation Medicine</strong>.
          </p>
          
          <p>
            I blend clinical practice with business strategy: Harvard-trained MD, Harvard MBA, former Chief Medical Officer at Modern Age, and product director at Virta Health. My mission? Help forward-thinking physicians build thriving longevity practices by mastering the <strong>SHIFT Framework</strong>—a systematic approach to the five pillars of aging biology.
          </p>
          
          <p>
            Today's deep-dive showcases how GLP-1 receptor agonists touch every SHIFT domain: from nutrient <strong>Sensing</strong> to cellular <strong>Homeostatic Defense</strong>, <strong>Inflammation</strong> control, <strong>Fibrosis</strong> prevention, and biological <strong>Timekeeping</strong>. This isn't just another drug class—it's a lens into the future of longevity medicine.
          </p>
          
          <p className="text-sm text-gray-600 italic">
            Let's explore how these remarkable compounds might extend not just lifespan, but healthspan—adding life to our years, not just years to our lives.
          </p>
        </div>
      )
    },
    {
      id: 2,
      title: "GLP-1 RAs – From Diabetes to Longevity",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            GLP-1 receptor agonists (GLP-1 RAs) have leapt from humdrum diabetes clinics to the forefront of longevity science. Originally prized for lowering blood sugar and body weight, they're now touted by some as potential "longevity pills".
          </p>
          
          <p>
            <strong>Why?</strong> Beyond glycemic control, GLP-1 RAs trigger a cascade of protective mechanisms – boosting mitochondrial function, enhancing cells' stress defenses, and quelling chronic inflammation. Remarkably, preclinical studies hint these drugs can extend lifespan in animal models by 10-20%, rivaling caloric restriction itself.
          </p>
          
          <p>
            But here's what's really exciting: unlike most longevity interventions stuck in the lab, GLP-1 RAs are already FDA-approved, widely prescribed, and accumulating real-world evidence of their broader health benefits. We're witnessing a rare convergence – a proven therapy that just happens to target the fundamental biology of aging.
          </p>

          <KeyTakeaway>
            GLP-1 receptor agonists represent a paradigm shift from treating individual diseases to addressing the underlying biology of aging itself. Their multi-system benefits suggest we may be witnessing the emergence of the first true "longevity drugs" in clinical practice.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 3,
      title: "The SHIFT Framework – Longevity's Pillars",
      content: (
        <div className="space-y-6">
          <p className="text-lg leading-relaxed">
            We'll examine GLP-1 agonists through the <strong>SHIFT</strong> lens – five pillars of aging biology: <strong>Sensing, Homeostatic Defense, Inflammation, Fibrosis,</strong> and <strong>Timekeeping</strong>. Each pillar represents processes that falter with age, and intriguingly, GLP-1 RAs touch them all.
          </p>
          
          <SHIFTFrameworkDiagram />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">Sensing</h4>
              <p className="text-sm text-blue-700">Metabolic "fuel gauges" like insulin pathways; GLP-1 modulates these to curb age-related metabolic issues.</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-green-800 mb-2">Homeostatic Defense</h4>
              <p className="text-sm text-green-700">Cellular clean-up and stress resistance; GLP-1 therapy boosts antioxidant defenses and autophagy.</p>
            </div>
            
            <div className="bg-red-50 p-4 rounded-lg">
              <h4 className="font-semibold text-red-800 mb-2">Inflammation</h4>
              <p className="text-sm text-red-700">The chronic "inflammaging" that drives tissue damage; GLP-1 RAs have notable anti-inflammatory effects.</p>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg">
              <h4 className="font-semibold text-purple-800 mb-2">Fibrosis</h4>
              <p className="text-sm text-purple-700">Organ scarring and stiffening; GLP-1 agonists protect against liver, heart, and kidney fibrosis.</p>
            </div>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-semibold text-yellow-800 mb-2">Timekeeping</h4>
            <p className="text-sm text-yellow-700">Both circadian rhythms and biological aging clocks; emerging evidence suggests GLP-1 RAs may help reset both.</p>
          </div>

          <KeyTakeaway>
            The SHIFT framework reveals why GLP-1 receptor agonists are so promising for longevity medicine – they simultaneously address multiple fundamental mechanisms of aging rather than targeting isolated symptoms or diseases.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 4,
      title: "Sensing – Caloric Excess as an Aging Accelerant",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            Cells sense nutrients via pathways (insulin/IGF-1, mTOR, AMPK) that double as aging regulators. Chronic caloric excess overstimulates these sensors, hastening aging, whereas caloric restriction (CR) slows aging's clock.
          </p>
          
          <p>
            Indeed, aligned with circadian rhythms, CR extended mouse lifespans by ~35%. In humans, even modest CR lowers fasting insulin and body temperature – biomarkers linked to longevity. High insulin and growth-factor signaling drive aging in many organisms; conversely, mutations that dampen these pathways often extend lifespan.
          </p>
          
          <p>
            The takeaway? Our cells' nutrient sensors are also aging timers. Overfeed them consistently, and you accelerate the aging process. This is where GLP-1 receptor agonists enter the picture – as sophisticated modulators of these ancient sensing systems.
          </p>

          <KeyTakeaway>
            The same pathways that sense nutrients also regulate aging. Chronic caloric excess accelerates aging through persistent activation of growth-promoting signals, while caloric restriction promotes longevity by allowing these pathways to reset and activate protective mechanisms.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 5,
      title: "Sensing – GLP-1 RAs as CR Mimetics",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            GLP-1 RAs have earned fame as weight-loss drugs, triggering sustained ~10–15% body weight reductions in many patients. Such weight loss isn't just cosmetic – it alleviates the metabolic overload that accelerates aging.
          </p>
          
          <GLP1SignalingDiagram />
          
          <p>
            In large outcome trials for Type 2 diabetes, GLP-1 RAs not only improved glucose control but cut the risk of major cardiovascular events by 14%, and even reduced all-cause mortality by ~12%. Fewer heart attacks and strokes today mean more people living to celebrate their 90th birthdays.
          </p>
          
          <p>
            But here's the kicker: GLP-1 RAs might be acting as "caloric restriction mimetics" – drugs that trigger the beneficial effects of caloric restriction without requiring people to actually restrict calories. They naturally reduce appetite and food intake while activating many of the same protective pathways triggered by CR.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-3">Clinical Outcomes</h4>
              <ul className="space-y-2 text-sm text-blue-700">
                <li>• 10-15% sustained weight loss</li>
                <li>• 14% reduction in CV events</li>
                <li>• 12% reduction in all-cause mortality</li>
                <li>• Improved insulin sensitivity</li>
                <li>• Lower inflammation markers</li>
              </ul>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-semibold text-green-800 mb-3">CR Mimetic Effects</h4>
              <ul className="space-y-2 text-sm text-green-700">
                <li>• Reduced caloric intake</li>
                <li>• Enhanced autophagy</li>
                <li>• Improved metabolic flexibility</li>
                <li>• Activated stress response pathways</li>
                <li>• Extended healthspan markers</li>
              </ul>
            </div>
          </div>

          <KeyTakeaway>
            GLP-1 receptor agonists may represent the first clinically available caloric restriction mimetics – drugs that provide the longevity benefits of caloric restriction without requiring people to actually restrict their food intake.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 6,
      title: "Sensing – Mimicking mTOR Inhibition (Rapamycin Parallel)",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            One of the most powerful longevity interventions in labs is rapamycin, a drug that inhibits mTOR, the cell's nutrient sensor. Strikingly, GLP-1 RAs appear to converge on similar longevity pathways.
          </p>
          
          <p>
            A recent multi-omic study in aged mice found that GLP-1 RA treatment produced a molecular "fingerprint" of rejuvenation closely resembling that of mTOR inhibition. In other words, GLP-1 agonism and rapamycin led to parallel changes in gene expression and metabolism, as if both were hitting the same anti-aging switches.
          </p>
          
          <p>
            This convergence suggests that GLP-1 RAs might be accessing some of the same fundamental longevity mechanisms as rapamycin – but through a completely different entry point. Rather than directly blocking mTOR, they seem to modulate its activity through upstream metabolic signals.
          </p>

          <KeyTakeaway>
            The molecular similarities between GLP-1 RA treatment and rapamycin suggest both interventions may be tapping into conserved longevity pathways, potentially making GLP-1 agonists a more clinically accessible alternative to direct mTOR inhibition.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 7,
      title: "Sensing – Hypothalamic Control of Aging",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            The hypothalamus is our body's master regulator for both metabolism and aging – it senses nutrients, orchestrates hormones, and even influences lifespan in animal studies. GLP-1 receptors are richly expressed in the hypothalamus, where they help signal satiety and energy availability.
          </p>
          
          <p>
            It turns out this brain region is a linchpin for GLP-1's anti-aging effects: blocking hypothalamic GLP-1 receptors blunted the rejuvenation seen in treated mice. It appears GLP-1 RAs engage the hypothalamus as a central hub, coordinating metabolic and aging processes throughout the body.
          </p>
          
          <p>
            This brain-centric mechanism explains why GLP-1 RAs have such broad effects – they're not just acting on individual organs, but reprogramming the master control center that governs energy balance, stress responses, and aging itself.
          </p>

          <KeyTakeaway>
            The hypothalamus serves as mission control for aging processes, integrating metabolic signals and coordinating responses throughout the body. GLP-1 receptor activation in this brain region appears to be the key to their system-wide anti-aging effects.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 8,
      title: "Homeostatic Defense – Autophagy & Cellular Cleanup",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            A hallmark of youthful cells is vigorous self-maintenance: removing junk, repairing damage. Autophagy – the process of chewing up and recycling damaged components – declines with age, contributing to cellular clutter and dysfunction.
          </p>
          
          <AutophagyDiagram />
          
          <p>
            GLP-1 RAs appear to revive this cleanup crew. In liver cell studies, the GLP-1 analog exendin-4 significantly cleared fat deposits by ramping up macroautophagy. Treated cells showed less ER stress and apoptosis, meaning less protein-folding chaos and fewer cells dying from toxic buildup.
          </p>
          
          <p>
            This autophagy boost isn't limited to the liver – similar effects have been observed in neurons, heart cells, and other tissues. By restoring cellular housekeeping, GLP-1 RAs help maintain the quality control systems that keep cells functioning optimally despite the accumulation of molecular damage over time.
          </p>

          <KeyTakeaway>
            GLP-1 receptor agonists appear to rejuvenate the cellular autophagy machinery, restoring the quality control systems that decline with age and helping cells maintain optimal function despite ongoing molecular damage.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 9,
      title: "Homeostatic Defense – Oxidative Stress & Senescence",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            Aging cells often suffer a double whammy: chronic oxidative stress and the buildup of senescent "zombie" cells. GLP-1 RAs activate the Nrf2 pathway – the master switch for antioxidant defenses.
          </p>
          
          <p>
            This boosts production of enzymes that neutralize free radicals, protecting DNA and mitochondria from damage that accumulates over decades. The ripple effects are profound: less oxidative stress means fewer cells pushed into senescence.
          </p>
          
          <p>
            Fascinatingly, GLP-1 has been shown to prevent endothelial senescence in blood vessels and reduce senescence markers in aged tissues. By keeping cells in a more youthful, functional state, GLP-1 RAs may help preserve tissue integrity and organ function throughout the aging process.
          </p>

          <KeyTakeaway>
            By activating multiple cellular defense mechanisms simultaneously, GLP-1 receptor agonists help maintain the cellular quality control systems that are essential for healthy aging and longevity.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 10,
      title: "Homeostatic Defense – Cardiovascular and Renal Resilience",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            "Homeostatic defense" isn't just within single cells – whole organs have maintenance circuits that GLP-1 RAs bolster. Take the cardiovascular system: GLP-1 agonists reduce harmful triglycerides and visceral fat that drive atherosclerosis.
          </p>
          
          <p>
            In animal models, they directly curbed plaque inflammation and slowed artery clogging. Clinically, GLP-1 RAs cut heart failure hospitalizations by ~12% in at-risk patients, suggesting the heart is staying stronger. Part of this may be improved diastolic function – the heart's ability to relax and fill properly between beats.
          </p>
          
          <p>
            Similarly, kidney protection is remarkable. GLP-1 RAs consistently slow progression of diabetic kidney disease, with some studies showing up to 35% reduction in kidney failure risk. This isn't just from better glucose control – direct protective effects on kidney cells appear to be involved.
          </p>

          <KeyTakeaway>
            GLP-1 receptor agonists provide organ-level homeostatic defense, protecting cardiovascular and renal systems through both metabolic improvements and direct protective effects on organ function.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 11,
      title: "Homeostatic Defense – Metabolic Balance Without Burnout",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            Decades of glucose spikes, insulin surges, and lipid overload leave metabolic organs exhausted. GLP-1 RAs help rebalance this system with a kind of gentle precision.
          </p>
          
          <p>
            They enhance insulin sensitivity and glucose metabolism, lowering the chronic glucotoxic and lipotoxic stress that can age the pancreas, liver, and arteries. Unlike aggressive therapies that hammer blood sugar down, GLP-1 RAs act in a glucose-dependent manner, smoothing out extremes without courting hypoglycemia.
          </p>
          
          <p>
            This "metabolic harmonization" may be why GLP-1 RAs preserve pancreatic beta-cell function over time, rather than burning out these insulin-producing cells. By reducing the metabolic burden on key organs, they help maintain long-term function and resilience.
          </p>

          <KeyTakeaway>
            Unlike harsh metabolic interventions, GLP-1 receptor agonists provide gentle, glucose-dependent metabolic regulation that preserves organ function and prevents the burnout associated with chronic metabolic stress.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 12,
      title: "Inflammation – Taming 'Inflammaging'",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            A slow, smoldering inflammation develops as we age – even without infections. This <em>inflammaging</em> is fueled by visceral fat, senescent cells, and genetic quirks like clonal hematopoiesis (CHIP) that pumps out inflammatory signals.
          </p>
          
          <p>
            It's a major driver of tissue damage in aging, linking to everything from atherosclerosis to Alzheimer's. GLP-1 RAs have a notable anti-inflammatory effect. By inducing weight loss and direct receptor actions, they lower levels of systemic inflammatory markers like C-reactive protein (CRP) and interleukin-6 (IL-6).
          </p>
          
          <p>
            But the anti-inflammatory effects go beyond simple weight loss. Even in lean individuals, GLP-1 RAs can reduce inflammation markers, suggesting direct anti-inflammatory properties independent of their metabolic effects.
          </p>

          <KeyTakeaway>
            GLP-1 receptor agonists combat inflammaging through both weight loss-dependent and independent mechanisms, addressing one of the fundamental drivers of age-related tissue damage and disease.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 13,
      title: "Inflammation – Immune Modulation by GLP-1",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            Zooming in, how do GLP-1 RAs quell inflammation? Research shows they act on multiple fronts of the immune system. GLP-1 receptors on certain T-cells and macrophages can skew these cells toward a less inflammatory state.
          </p>
          
          <InflammationPathwayDiagram />
          
          <p>
            In rodents, GLP-1 signaling in the brain is actually required to fully suppress peripheral inflammation triggered by Toll-like receptor agonists (like bacterial endotoxin). This neuro-immune crosstalk hints that GLP-1 RAs engage the vagus nerve and autonomic pathways to calm systemic inflammation.
          </p>
          
          <p>
            The result is a sophisticated immune modulation that doesn't suppress immunity entirely, but rather rebalances it away from chronic, damaging inflammation toward more appropriate, controlled responses.
          </p>

          <KeyTakeaway>
            GLP-1 receptor agonists modulate immune function through multiple pathways, including direct effects on immune cells and neuro-immune crosstalk, providing sophisticated anti-inflammatory benefits without compromising essential immune responses.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 14,
      title: "Inflammation – Neuroinflammation and Neurodegeneration",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            Chronic inflammation in the brain is a known accelerant of neurodegenerative diseases. GLP-1 RAs are now being explored as neuroprotective agents, in part for their anti-inflammatory prowess in the CNS.
          </p>
          
          <p>
            Alzheimer's disease, for example, is sometimes dubbed "Type 3 diabetes" due to links with metabolic dysfunction. GLP-1 RAs (liraglutide, semaglutide) have shown promise in animal models of AD by reducing neuroinflammatory cytokines and amyloid burden.
          </p>
          
          <p>
            Notably, a post-hoc analysis of diabetes trials found that GLP-1 RA users had lower rates of dementia diagnosis compared to other diabetes medications. While not definitive proof, these signals suggest potential cognitive protection through neuroinflammation reduction.
          </p>

          <KeyTakeaway>
            The neuroprotective potential of GLP-1 receptor agonists, including their ability to reduce neuroinflammation, suggests they may help protect against age-related cognitive decline and neurodegenerative diseases.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 15,
      title: "Inflammation – A Broad Anti-Senescence Net",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            Because aging and inflammation intertwine so deeply, anti-inflammatory therapies act as de facto anti-aging tools. GLP-1 RAs, by lowering inflammation, also tackle downstream effects like fibrosis and cellular senescence – all major aging accelerators.
          </p>
          
          <p>
            A recent review highlighted that GLP-1 agonists can protect against chronic inflammation, oxidative stress, and even cellular senescence. How might a gut hormone analog influence something like cellular senescence? Consider that senescent cells are often inflammatory cells – they secrete pro-inflammatory factors that damage surrounding tissues.
          </p>
          
          <p>
            By reducing the inflammatory burden that drives cells into senescence and by potentially clearing existing senescent cells, GLP-1 RAs cast a broad anti-senescence net that addresses multiple aging pathways simultaneously.
          </p>

          <KeyTakeaway>
            The anti-inflammatory effects of GLP-1 receptor agonists create a cascade of anti-aging benefits, reducing cellular senescence and breaking the cycle of inflammation that accelerates multiple aging processes.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 16,
      title: "Fibrosis – Scars and Aging",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            Many of aging's worst illnesses boil down to fibrosis: the excessive scarring and stiffening of tissues. Heart failure with age is tied to myocardial fibrosis; chronic kidney disease involves renal scarring; idiopathic pulmonary fibrosis and liver cirrhosis are obviously fibrotic.
          </p>
          
          <p>
            Aging itself predisposes organs to fibrose – the prevalence of cardiac and lung fibrosis climbs dramatically with advancing age. Why? Senescent cells and chronic inflammation drive collagen deposition and tissue remodeling that gradually replaces functional tissue with scar.
          </p>
          
          <p>
            Fibrosis represents a fundamental aging mechanism – the gradual replacement of functional tissue architecture with dysfunctional scar tissue. This is why anti-fibrotic interventions are increasingly viewed as potential longevity therapies.
          </p>

          <KeyTakeaway>
            Fibrosis represents a core aging mechanism where functional tissue is gradually replaced by scar tissue. Understanding and preventing fibrosis is essential for maintaining organ function and extending healthspan.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 17,
      title: "Fibrosis – Fatty Liver to Healthy Liver",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            Non-alcoholic fatty liver disease (NAFLD) and its advanced form NASH (non-alcoholic steatohepatitis) are quintessential fibrotic diseases of aging, tightly linked to obesity and diabetes. Two-thirds of diabetic older adults have NAFLD/NASH, and over decades fatty liver can progress to cirrhosis (scarred liver) or liver cancer.
          </p>
          
          <p>
            GLP-1 RAs offer a beacon of hope here. A meta-analysis showed GLP-1 therapy can resolve NAFLD, reducing liver fat and inflammation. In a pivotal trial, semaglutide achieved NASH resolution without worsening fibrosis in 59% of patients – a remarkable result for a condition with few effective treatments.
          </p>
          
          <p>
            This liver protection isn't just about weight loss. GLP-1 RAs appear to have direct hepatoprotective effects, reducing lipotoxicity, inflammation, and the stellate cell activation that drives liver fibrosis.
          </p>

          <KeyTakeaway>
            GLP-1 receptor agonists demonstrate remarkable efficacy in treating and reversing fatty liver disease, one of the most common fibrotic conditions of aging, through both metabolic and direct hepatoprotective mechanisms.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 18,
      title: "Fibrosis – Heart and Kidney Protection",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            Heart and kidney fibrosis are silent agers – you don't notice them until organ function falls off a cliff. Encouragingly, GLP-1 RAs have shown they can slow or prevent these fibrotic declines.
          </p>
          
          <p>
            In diabetics, GLP-1 therapy has consistently protected kidney function: trials report up to a 30–35% reduction in progression to hard kidney outcomes (like end-stage disease). This kidney-sparing effect is attributed to less glomerular strain (lower blood pressure, improved blood sugar control) and direct anti-fibrotic actions on kidney cells.
          </p>
          
          <p>
            Similarly, heart failure outcomes improve with GLP-1 RAs, likely through reduced cardiac fibrosis and better diastolic function. The heart doesn't just pump better – it stays more flexible and responsive over time.
          </p>

          <KeyTakeaway>
            GLP-1 receptor agonists provide significant protection against heart and kidney fibrosis, preserving organ function and preventing the silent deterioration that often goes unnoticed until severe dysfunction occurs.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 19,
      title: "Fibrosis – Next-Gen Agents (Tirzepatide, Retatrutide)",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            The fibrotic diseases of aging may meet their match in next-generation multi-agonist drugs. Tirzepatide (a dual GIP/GLP-1 agonist) and retatrutide (a triple GIP/GLP-1/Glucagon agonist) amplify the benefits we see with GLP-1 alone.
          </p>
          
          <p>
            In a recent trial, retatrutide delivered jaw-dropping metabolic results: 48 weeks of treatment led to ~24% body weight reduction – rivaling bariatric surgery – at the highest dose. Even more impressively, in patients with fatty liver, retatrutide cut liver fat by a staggering 82%.
          </p>
          
          <p>
            These aren't just incremental improvements – they represent quantum leaps in our ability to reverse metabolic dysfunction and prevent organ fibrosis. The anti-fibrotic potential of these next-generation agents may prove transformative for age-related organ decline.
          </p>

          <KeyTakeaway>
            Next-generation multi-agonist drugs like tirzepatide and retatrutide deliver unprecedented metabolic benefits and anti-fibrotic effects, potentially representing transformative tools for preventing age-related organ decline.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 20,
      title: "Spotlight: Retatrutide Chemistry – the Triple Threat",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            One 39-amino-acid peptide, three targets. Retatrutide grafts GLP-1 motifs onto an oxyntomodulin backbone, then tweaks two positions for GIP bias and adds a C-terminal fatty di-acid to stretch its half-life to ~6 days.
          </p>
          
          <MultiAgonistDiagram />
          
          <p>
            The result: near-maximal occupancy at GLP-1, GIP, and glucagon receptors with a single weekly shot. That triple play matters. GLP-1 curbs appetite. GIP amplifies insulin and seems to blunt nausea. Glucagon flips brown-fat UCP-1 to "on," lifting resting energy expenditure.
          </p>
          
          <p>
            Chemically, it's a molecular Swiss-army knife—one handle, three blades—handing clinicians unprecedented leverage over metabolic flux.
          </p>

          <KeyTakeaway>
            Retatrutide's sophisticated molecular design enables simultaneous activation of three key metabolic receptors, creating synergistic effects that far exceed what's possible with single-target approaches.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 21,
      title: "Mechanistic Edge – Why Tri-Agonism Outperforms Mono",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            Glucagon agonism raises energy expenditure by ~100 kcal/day even in humans, independent of BAT activation. GIP co-signaling tempers GLP-1–induced nausea, lets doses climb higher, and adds its own insulin-sensitizing push.
          </p>
          
          <p>
            Pre-clinical tri-agonists beat mono- or dual-agents on weight, glycemia, and lipid oxidation. In effect, retatrutide attacks energy balance from both sides: less in, more out. Add the GLP-1 "brake" on gastric emptying and you have a metabolic pincer move that slashes liver fat (82% in 24 wk) and improves HOMA-IR beyond weight loss alone.
          </p>
          
          <p>
            The physiology reads like tight jazz-trio improvisation—three hormones riffing around a single metabolic theme.
          </p>

          <KeyTakeaway>
            Triple receptor agonism creates synergistic metabolic effects that exceed the sum of individual components, attacking energy balance from multiple angles simultaneously.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 22,
      title: "SHIFT Scorecard – Retatrutide vs Sema / Tirze",
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-800 mb-2">Sensing: A+</h4>
              <p className="text-sm text-green-700">-24 kg in 48 wk; plateau-free curve</p>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-2">Homeostatic Defence: A</h4>
              <p className="text-sm text-blue-700">apoC-III ↓ 40%, HOMA-IR ↓ 50%</p>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h4 className="font-semibold text-yellow-800 mb-2">Inflammation: B+</h4>
              <p className="text-sm text-yellow-700">CRP ↓ 30%; IL-6 trending down</p>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-semibold text-purple-800 mb-2">Fibrosis: A-</h4>
              <p className="text-sm text-purple-700">liver fat ↓ 82%, early NASH resolution signals</p>
            </div>
            
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h4 className="font-semibold text-orange-800 mb-2">Timekeeping: B</h4>
              <p className="text-sm text-orange-700">multi-omic rejuvenation in mice; circadian data pending</p>
            </div>
          </div>
          
          <p className="text-lg leading-relaxed mt-4">
            The tri-agonist edges past semaglutide on every dial except long-term safety—still a question mark. For the clinician, the message is simple: if SHIFT is your dashboard, retatrutide lights up almost every gauge green.
          </p>

          <KeyTakeaway>
            Retatrutide demonstrates superior performance across all five SHIFT framework pillars compared to single or dual agonists, though long-term safety remains to be established.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 23,
      title: "Fiber – The Unsung Co-Therapy",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            Viscous, fermentable fibers aren't just bulk; they're substrates for gut bacteria to spin into SCFAs—especially propionate and butyrate. Those SCFAs hit GPR43 on L-cells, boosting endogenous GLP-1 release and deepening satiety.
          </p>
          
          <p>
            Clinically, high-fiber diets raise fasting GLP-1 by ~25% and blunt post-prandial glucose spikes. For patients on GLP-1 RAs, fiber is a side-kick that (a) smooths GI tolerance, (b) mitigates constipation, (c) preserves lean mass by nurturing butyrate-fed colonocytes, and (d) may allow lower drug doses for the same metabolic punch.
          </p>
          
          <p>
            In other words, don't hand someone a Ferrari and skip the premium fuel—fiber is that fuel.
          </p>

          <KeyTakeaway>
            Fiber acts as a natural GLP-1 enhancer through SCFA production, potentially allowing for lower drug doses while improving tolerance and preserving lean mass during treatment.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 24,
      title: "Practical Protocol – Retatrutide + Fiber Stack",
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
            <h4 className="font-semibold mb-4">Clinical Protocol:</h4>
            <ol className="space-y-3 text-sm">
              <li><strong>1.</strong> Start tirzepatide 1.25 mg weekly; uptitrate every 4 wk as tolerated.</li>
              <li><strong>2.</strong> Prescribe ≥30 g/day mixed soluble fiber (psyllium + inulin).</li>
              <li><strong>3.</strong> Track fasting insulin, ALT, HRV, and sleep onset latency monthly; expect HRV ↑ 8–12 ms by month 3.</li>
              <li><strong>4.</strong> Advise 1.6 g/kg protein and ≥2 resistance sessions per week to guard muscle.</li>
              <li><strong>5.</strong> Re-image liver fat at 24 wk; if &lt;5%, consider maintenance dose 4 mg.</li>
            </ol>
          </div>
          
          <p className="text-lg leading-relaxed">
            This stack targets all SHIFT pillars—caloric sensing, hepatic defense, anti-inflammation, anti-fibrosis, and circadian tidiness—while keeping side-effects in check.
          </p>

          <KeyTakeaway>
            A systematic protocol combining tirzepatide with fiber supplementation and lifestyle modifications can optimize outcomes across all SHIFT framework domains while minimizing adverse effects.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 25,
      title: "Caveats & Mitigations",
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <h4 className="font-semibold text-red-800 mb-2">Lean-mass drift (-8%)</h4>
              <p className="text-sm text-red-700">→ emphasize progressive overload + protein</p>
            </div>
            
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h4 className="font-semibold text-orange-800 mb-2">GI symptoms (30% of patients)</h4>
              <p className="text-sm text-orange-700">→ low-FODMAP start, fiber ramp 10 → 30 g over 3 wk</p>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h4 className="font-semibold text-yellow-800 mb-2">Transient thyroid C-cell signals</h4>
              <p className="text-sm text-yellow-700">→ monitor calcitonin yearly in humans</p>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-2">Micronutrient gaps</h4>
              <p className="text-sm text-blue-700">→ track B12, folate, iron at baseline and 6 mo</p>
            </div>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mt-4">
            <h4 className="font-semibold text-purple-800 mb-2">Unknown decades-long safety</h4>
            <p className="text-sm text-purple-700">→ frame therapy as renewable 1- to 2-year cycles with holiday periods for surveillance</p>
          </div>
          
          <p className="text-lg leading-relaxed mt-4">
            None of these are deal-breakers, but each demands respect. A longevity drug that ignores longevity safety isn't a longevity drug at all.
          </p>

          <KeyTakeaway>
            While GLP-1 agonists show remarkable promise for longevity medicine, careful monitoring and risk mitigation strategies are essential for safe long-term use.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 26,
      title: "Timekeeping – Circadian Rhythms and Longevity",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            Our bodies tell time in multiple ways. There's the daily circadian rhythm governed by the brain's clock (and peripheral clocks in each organ), and there's the longer arc of biological age measured in molecules.
          </p>
          
          <p>
            Both kinds of "timekeeping" influence lifespan. Disrupted circadian rhythms – say from chronic jet lag or erratic sleep – are associated with metabolic diseases and shorter lifespans. Conversely, aligning feeding and activity to the body's clock can extend lifespan in lab animals.
          </p>
          
          <p>
            This is where GLP-1's role as a meal-timed hormone becomes particularly intriguing for longevity applications.
          </p>

          <KeyTakeaway>
            Both circadian rhythms and biological aging clocks influence longevity, making GLP-1's role as a meal-timed hormone particularly relevant for optimizing both temporal regulatory systems.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 27,
      title: "Timekeeping – GLP-1 and Circadian Synergy",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            GLP-1 is a hormone normally released with meals, so it's inherently tied to feeding times – a major Zeitgeber (time-giver) for our peripheral clocks. There is emerging evidence that GLP-1-based therapy can reinforce healthy circadian patterns.
          </p>
          
          <p>
            In a fascinating experiment, researchers used exogenous GLP-1 injections alongside time-restricted feeding in a mouse model of Alzheimer's (which typically has disrupted circadian rhythm). The combination <strong>improved the overall circadian function</strong> and cognitive outcomes compared to either intervention alone.
          </p>
          
          <p>
            This suggests GLP-1 RAs might serve dual roles: metabolic regulators and circadian optimizers.
          </p>

          <KeyTakeaway>
            GLP-1 receptor agonists may enhance circadian rhythm optimization, particularly when combined with time-restricted feeding, offering dual benefits for metabolic and temporal regulation.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 28,
      title: "Timekeeping – Slowing the Biological Clock (Epigenetics)",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            Beyond daily rhythms, does GLP-1 agonism slow the <em>biological</em> clock – the molecular aging measured by epigenetic changes and gene expression? One groundbreaking study suggests yes.
          </p>
          
          <p>
            In aged mice, GLP-1 RA treatment not only improved physical function but <strong>reversed age-related molecular profiles</strong> across multiple organs. Tissues from GLP-1–treated old mice showed transcriptomic and DNA methylation patterns shifting toward a more youthful state.
          </p>
          
          <p>
            In fact, the researchers noted that the molecular rejuvenation was so pronounced that treated old mice resembled young mice in many key aging biomarkers. This represents potential true biological age reversal, not just disease prevention.
          </p>

          <KeyTakeaway>
            GLP-1 receptor agonists may actually reverse biological aging at the molecular level, with treated aged mice showing youthful patterns in transcriptomic and epigenetic markers.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 29,
      title: "Timekeeping – Chronotherapy and Future Directions",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            GLP-1 RAs might eventually be deployed in a <em>chronotherapeutic</em> fashion – aligning dosing with body clock or combining with lifestyle to maximize benefits.
          </p>
          
          <p>
            For instance, administering the drug to reinforce morning satiety could help someone restrict food intake to earlier in the day, amplifying circadian alignment benefits (since eating late at night impairs metabolic rhythm).
          </p>
          
          <p>
            It's an intriguing idea that an anti-diabetes drug could become a tool for circadian optimization. Studies are underway examining optimal timing of GLP-1 RA dosing relative to meals and sleep cycles.
          </p>

          <KeyTakeaway>
            Future chronotherapeutic approaches may optimize GLP-1 agonist timing to enhance both metabolic benefits and circadian rhythm alignment, maximizing longevity potential.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 30,
      title: "Future – Macrodosing GLP-1 for Longevity",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            As we cast forward, one provocative idea is <strong>macrodosing</strong> GLP-1 pathway agents – using high-powered analogs in higher-risk or even "pre-risk" individuals to delay aging.
          </p>
          
          <p>
            Consider retatrutide's dramatic efficacy: ~24% weight loss and ~82% liver fat reduction in 48 weeks. These are figures that rewrite what we thought possible short of surgery. It raises a question: could a person in midlife, with creeping weight and fatty liver, take a potent triple-agonist for a year to essentially rewind their metabolic clock by decades?
          </p>
          
          <p>
            Some longevity experts are already imagining a future where these drugs are used not just to treat diabetes or obesity, but to <strong>prevent</strong> them – keeping people metabolically youthful into old age. "Macrodosing" implies pushing the dose to achieve maximum rejuvenation, potentially using GLP-1 RAs as a sort of <em>metabolic statin</em>, started in one's 40s or 50s to forestall chronic diseases before they manifest.
          </p>

          <KeyTakeaway>
            The concept of "macrodosing" GLP-1 agonists represents a paradigm shift toward using these agents proactively for longevity and disease prevention, rather than just disease treatment.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 31,
      title: "Conclusion – Shifting to a Longevity Paradigm",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            GLP-1 receptor agonists illustrate how a single intervention can simultaneously tackle multiple pillars of aging. Through the <strong>SHIFT</strong> framework, we've seen GLP-1 agonism recalibrate nutrient sensing, bolster homeostatic defenses, cool inflammation, mitigate fibrosis, and even retune biological clocks.
          </p>
          
          <p>
            This is a remarkable breadth of effect for drugs initially developed just to lower glucose. Of course, no panacea exists – GLP-1 RAs won't make us immortal (and they won't replace exercise and diet). But they do offer a glimpse of pharmacological longevity medicine that is evidence-backed and here today.
          </p>
          
          <p>
            By helping people lose weight, avoid diabetes, and maintain healthier organs, GLP-1 RAs may already be extending healthy lifespan in practice. The task now is to integrate such therapies thoughtfully – to truly shift our approach from treating disease late to maintaining health long.
          </p>
          
          <p>
            In doing so, we just might add both years to life and life to years, with a little help from our incretin friends.
          </p>

          <KeyTakeaway>
            GLP-1 receptor agonists represent a paradigm shift from reactive disease treatment to proactive health maintenance, offering a comprehensive approach to healthy aging through the SHIFT framework.
          </KeyTakeaway>
        </div>
      )
    },
    {
      id: 32,
      title: "Your Next Step – SHIFT Mentorship (Webinar Offer)",
      content: (
        <div className="space-y-4">
          <p className="text-lg leading-relaxed">
            If today's tour left you hungry for the full playbook, join my <strong>SHIFT Mentorship Program</strong>—a six-month immersive that turns this framework into daily clinical mastery.
          </p>
          
          <p>
            For anyone watching this webinar, the first-week window comes with a <strong>$1,000 tuition credit</strong> and <strong>10% off</strong> consulting retainers for the first three months. The cohort starts next month; seats are capped to keep discussions surgical.
          </p>
          
          <p>
            Scan the QR code now or visit <strong>www.nextgenerationmedicine.co/mentorship</strong> to secure your spot. Don't let another year pass wondering "what if" about longevity medicine—make this the year you master it.
          </p>
          
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-6 rounded-lg text-center">
            <h4 className="text-xl font-bold mb-2">Ready to Transform Your Practice?</h4>
            <p className="mb-4">Join the SHIFT Mentorship Program Today</p>
            <div className="text-sm opacity-90">
              www.nextgenerationmedicine.co/mentorship
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <MentorshipAccessGate>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        <main className="flex-grow">
          <div className="container mx-auto px-4 py-8 max-w-5xl">
            {/* Breadcrumbs */}
            <Breadcrumb className="mb-6">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href="/" className="flex items-center">
                    <Home className="h-4 w-4 mr-1" />
                    Home
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink href="/mentorship">Mentorship</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink href="/mentorship/content">Lectures</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <span className="text-gray-500">Microdosing GLP-1s 2.0</span>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50]">
                  Microdosing GLP-1s 2.0
                </h1>
                <div className="bg-gradient-to-r from-green-500 to-blue-500 text-white px-3 py-1 rounded-full flex items-center text-sm">
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  <span>Mentorship Content</span>
                </div>
              </div>
              
              <p className="text-lg text-[#7F8C8D] mb-4">
                Advanced GLP-1 Receptor Agonist Strategies Through the SHIFT Framework for Longevity Medicine
              </p>
              
              <div className="flex items-center gap-4 text-sm text-[#7F8C8D]">
                <div className="flex items-center">
                  <FileText className="h-4 w-4 mr-1" />
                  <span>32 Slides</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  <span>Updated January 2025</span>
                </div>
              </div>
            </div>
            
            <Separator className="mb-6" />
            
            <Slideshow slides={lectureSlides} />
          </div>
        </main>
        
        <Footer />
      </div>
    </MentorshipAccessGate>
  );
};

export default MicrodosingGLP1s2;